import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import LoginIcon from '@mui/icons-material/Login';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

import { googleLogout } from '@react-oauth/google';
import SearchForm from './SearchForm';
import useRedirectToLogin from '../hooks/useRedirectToLogin';
import { useColorMode } from '../theme/ThemeContext';

googleLogout();

function Header() {
  const [isWebView, setIsWebView] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const redirectToLogin = useRedirectToLogin();
  const token = localStorage.getItem('aircheck_access');
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { mode, toggleMode } = useColorMode();

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const isWebViewParam = query.get('wv');
    setIsWebView(isWebViewParam === 'true/');
  }, []);

  const goBack = () => navigate(-1);
  const handleLogin = () => redirectToLogin();
  const handleLogout = () => {
    localStorage.removeItem('aircheck_access');
    localStorage.removeItem('aircheck_refresh');
    googleLogout();
    setAnchorEl(null);
    navigate('/login');
  };

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const goTo = (path) => {
    setAnchorEl(null);
    setDrawerOpen(false);
    navigate(path);
  };

  if (isWebView) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <SearchForm />
      </Container>
    );
  }

  return (
    <>
      <AppBar position="sticky" elevation={0} color="default">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: 1.5, py: 1 }}>
            {!isHome && (
              <Tooltip title="Back">
                <IconButton onClick={goBack} size="small">
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            )}

            <Box
              onClick={() => navigate('/')}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'baseline', gap: 0.5, mr: 1 }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 900,
                  letterSpacing: '-0.02em',
                  background: 'linear-gradient(135deg,#E50914,#FF4D5E)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1,
                }}
              >
                AirCheck
              </Typography>
            </Box>

            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
              <SearchForm compact />
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
                <IconButton onClick={toggleMode} size="small">
                  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>

              {token ? (
                <>
                  <Tooltip title="Watchlist">
                    <IconButton onClick={() => navigate('/watchlist')} size="small">
                      <BookmarkBorderIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Reminders">
                    <IconButton onClick={() => navigate('/reminder')} size="small">
                      <NotificationsIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Account">
                    <IconButton onClick={openMenu} size="small">
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}>
                        <PersonOutlineIcon fontSize="small" />
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                    <MenuItem onClick={() => goTo('/profile')}>
                      <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
                      Profile
                    </MenuItem>
                    <MenuItem onClick={() => goTo('/watchlist')}>
                      <ListItemIcon><BookmarkBorderIcon fontSize="small" /></ListItemIcon>
                      Watchlist
                    </MenuItem>
                    <MenuItem onClick={() => goTo('/reminder')}>
                      <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
                      Reminders
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                      Sign out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Tooltip title="Sign in">
                  <IconButton onClick={handleLogin} size="small">
                    <LoginIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
                <IconButton onClick={toggleMode} size="small">
                  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setDrawerOpen(true)} size="small">
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>

          <Box sx={{ display: { xs: 'block', md: 'none' }, pb: 1.5 }}>
            <SearchForm compact />
          </Box>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260, pt: 2 }} role="presentation">
          <Typography variant="overline" sx={{ pl: 2, color: 'text.secondary' }}>
            Menu
          </Typography>
          <List>
            {token ? (
              <>
                <ListItemButton onClick={() => goTo('/profile')}>
                  <ListItemIcon><PersonOutlineIcon /></ListItemIcon>
                  <ListItemText primary="Profile" />
                </ListItemButton>
                <ListItemButton onClick={() => goTo('/watchlist')}>
                  <ListItemIcon><BookmarkBorderIcon /></ListItemIcon>
                  <ListItemText primary="Watchlist" />
                </ListItemButton>
                <ListItemButton onClick={() => goTo('/reminder')}>
                  <ListItemIcon><NotificationsIcon /></ListItemIcon>
                  <ListItemText primary="Reminders" />
                </ListItemButton>
                <Divider sx={{ my: 1 }} />
                <ListItemButton onClick={handleLogout}>
                  <ListItemIcon><LogoutIcon /></ListItemIcon>
                  <ListItemText primary="Sign out" />
                </ListItemButton>
              </>
            ) : (
              <ListItemButton onClick={handleLogin}>
                <ListItemIcon><LoginIcon /></ListItemIcon>
                <ListItemText primary="Sign in" />
              </ListItemButton>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default Header;
